import {
  BulkImportMutationHookResult,
  BulkUpdatePostsMutationHookResult,
  CoreMediaLibraryItemFieldsFragment,
  CreatePostMutationHookResult,
  GetBitlyAccountsQuery,
  GetLinkMetadataQuery,
  GetPostsEditorQuery,
  LinkDestination,
  LinkGoogleCallToAction,
  PostFeedType,
  PostMultiMediaAttachMode,
  PostPublishMethod,
  PostQueuePosition,
  PostSource,
  PostStatus,
  PostTiming,
  TimezoneSetting,
  UtmInput
} from './graphql';
import { PostEditorErrorKeys } from './validator-props';
import { CustomParagraph } from './slate-editor';
import { SelectedSocialProfileValue } from '../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { MediaUploadStatus } from '../constants/media';
import { SocialProfileWithBoardsAndMentionType } from '../modules/PostEditor/helpers/mapSelectedProfilesValues';
import { ErrorMessageSeverity } from '../constants/form';
import { OnClosePostEditorAction } from '../providers/AppProvider';
import { DeepReadonly } from './basic-objects';
import { BulkActionsState } from './bulk-actions';
import { MutableRefObject } from 'react';
import { MappedProfileType } from '../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';

export enum PostEditorContextEnum {
  'undefined' = 0,
  'new',
  'edit',
  'import',
  'reschedule_edit', // we call this when we reshchedule a FAILED post
  'reschedule_new', // we call this when we reshchedule a PAST post
  'duplicate',
  'restore_trashed'
}

export enum PostEditorRecurringIntervalType {
  'days' = 'days',
  'weeks' = 'weeks',
  'years' = 'years'
}

export type PostOnDateObject = { date: Date; time: Date; isPast: boolean };

export type ScheduledDateForm = {
  date: Date;
  hour: string;
  minute: string;
  ampm?: 'am' | 'pm';
};

export type DisconnectedProfilesType = {
  id: string;
  shown: boolean;
  selected: boolean;
  __typename: MappedProfileType['__typename'];
};

export type VariationMeta = {
  currentVariationIndex: number;
  prevPostedCount: number;
  total: number;
  mediaVariationIndex: null | number;
  textVariationIndex: null | number;
};

export type PostEditorInitialFn = () => PostEditorState;
export type InitialParagrafFn = () => CustomParagraph;
export type InitialBulkActionsFn = () => BulkActionsState;

export type PostEditorState = DeepReadonly<{
  loading: boolean;
  postGroups: PostGroupsType[];
  shouldValidate: boolean;
  isValid: boolean | undefined;
  disconnectedProfiles: DisconnectedProfilesType[];
}>;

export type PostEditorPostErrors = false | PostEditorPostError | PostEditorPostError[];
export type PostEditorErrors = { [key in PostEditorErrorKeys]: PostEditorPostErrors };

export type PostEditorPostError = {
  selectedSocialProfiles: SelectedSocialProfileValue[];
  message: string;
  profileId: null | string; //
  variationId: null | number;
  severity: ErrorMessageSeverity;
};

export type PostEditorBitly = DeepReadonly<{
  profilesWithBitly: GetBitlyAccountsQuery['workspace']['socialProfiles'];
  defaultBitly: GetBitlyAccountsQuery['workspace']['settings']['defaultBitly'];
}>;

export type PostGroupsType = DeepReadonly<{
  bitlyAccount: PostEditorBitly;
  activeCustomizeIndex: null | number;
  categoryId: string | null;
  familyUuid: null | string;
  customizeContentPerProfile: boolean;
  customizeSchedulePerProfile: boolean;
  groupPostAttributes: GroupPostAttributesType;
  posts: GroupPostAttributesType[];
  readOnlyPosts: ReadonlyGroupPostAttributesType[];
  profiles: SelectedSocialProfileValue[];
  errors: PostEditorErrors;
  expanded: boolean;
  deleted: boolean;
}>;

export type EvergreenType = {
  active: boolean;
  expiryMaxRepeats: number | null;
  expiryDate: string | null; //ISO-8601 encoded date string
  expireType: Array<'date' | 'count'>;
};

export type RecurringType = EvergreenType & {
  date: string | null; //ISO-8601 encoded date string
};

export type EditorText = {
  text: CustomParagraph[];
  id: number;
};

export type PostEditorLink = {
  initialUrl: string; // requested url preprended with http if needed;
  googleCallToAction: LinkGoogleCallToAction;
  destination: LinkDestination;
  url: string; // url is the url after we do detection, the one we get from the DB as the URL
  useBitly: boolean;
  utmTracking: UtmInput | null;
  addUtm: boolean;
  removeUtm: boolean;
  removePreview: boolean;
  linkMetadata: GetLinkMetadataQuery['linkMetadata'][number] | undefined;
};

export type GroupPostAttributesContentType = DeepReadonly<{
  firstComment: CustomParagraph[];
  link: PostEditorLink | null;
  media: MediaTypePostEditorState[];
  postFeedType: PostFeedType;
  publishMethod: PostPublishMethod | null;
  shouldShowFirstComment: boolean;
  texts: EditorText[];
  title: string | null;
  variationMeta: VariationMeta;
  varyMedia: null | PostMultiMediaAttachMode;
}>;

export type ScheduledDateTimesType = {
  dateTime: string; //ISO-8601 encoded date string
  isPast: boolean;
};

export type GroupPostAttributesSchedulingType = DeepReadonly<{
  evergreen: EvergreenType;
  postTiming: PostTiming;
  queuePosition: null | PostQueuePosition | 'CURRENT';
  recurring: RecurringType;
  recurringType: PostEditorRecurringIntervalType | null;
  recurringValue: number | null; //ISO-8601 encoded date string
  scheduledLocalDateTimes: ScheduledDateTimesType[];
  scheduledTimezone: null | TimezoneSetting;
}>;

export type GroupPostAttributesType = DeepReadonly<{
  content: GroupPostAttributesContentType;
  customizedProfileId: null | string;
  postId: null | string;
  parentId: null | string;
  postStatus: PostStatus;
  postSource: PostSource | null;
  scheduling: GroupPostAttributesSchedulingType;
}>;

export type ReadonlyGroupPostAttributesType = GroupPostAttributesType & { readonly active: boolean };

export type MediaTypePostEditorState = Pick<
  CoreMediaLibraryItemFieldsFragment,
  'id' | 'type' | 'url' | 'thumbnailUrl' | '__typename' | 'proportions' | 'width' | 'height' | 'filesize'
> & {
  loading: boolean;
  status: MediaUploadStatus;
  gif: boolean;
  length: number | null;
  transcoded: boolean;
  externalUpload: boolean;
};

export type PostGroupSubmitData = {
  //
  evergreen: {
    expireAfterCount: number;
    expireAfterDate: Date;
    expireType: EvergreenType['expireType'];
  };
  recurring: {
    expireAfterCount: number;
    expireAfterDate: Date;
    expireType: EvergreenType['expireType'];
  };
  repeat: {
    intervalType: PostEditorRecurringIntervalType;
    intervalValue: string;
    postOn: {
      date: Date;
      minute: string;
      hour: string;
    };
  };
  schedule: {
    date: Date;
    hour: string;
    minute: string;
  }[];
  postTiming: PostTiming;
  queuePosition: PostQueuePosition | 'CURRENT';
};

export type PostEditorSubmitData = {
  addAnother: Array<'on'>;
  categoryId: string;
  PostSource: PostSource;
  customizeContentPerProfile: undefined | boolean;
  customizeSchedulePerProfile: undefined | boolean;
  globalPostStatus: PostStatus;
  profiles: Array<string | boolean>;
  select_all_profiles: boolean;
  profiles_pinterestBoard: Array<string | boolean | undefined>;
  postGroup: PostGroupSubmitData[];
};

export type PostEditorSubmitErrorPayload = {
  type: 'single' | 'bulk';
};

export type PostEditorSubmitPayload = {
  submitted: MutableRefObject<boolean>;
  profiles: SocialProfileWithBoardsAndMentionType[];
  defaultQueuePosition: PostQueuePosition;
  data: PostEditorSubmitData;
  callback?: OnClosePostEditorAction;
  onPlanLimitException?: VoidFunction;
  workspaceId: string;
  ref: MutableRefObject<HTMLDivElement | null>;
} & (
  | {
      type: 'single';
      context: 'create';
      updatePostsHookResult?: never;
      fetchedFamily?: never;
      createPostHookResult: CreatePostMutationHookResult;
      bulkImportHookResult?: never;
    }
  | {
      type: 'single';
      context: 'update';
      updatePostsHookResult: BulkUpdatePostsMutationHookResult;
      fetchedFamily: GetPostsEditorQuery['latestFamilyMembersOfPost'];
      createPostHookResult: CreatePostMutationHookResult;
      bulkImportHookResult?: never;
    }
  | {
      type: 'bulk';
      context: 'create';
      updatePostsHookResult?: never;
      fetchedFamily?: never;
      createPostHookResult?: never;
      bulkImportHookResult: BulkImportMutationHookResult;
    }
);
